import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { BaseEntity } from '../base/BaseEntity'
import { modelOptions, Prop, PropType } from '@typegoose/typegoose'
import { Address } from '../address/Address'
import { Customer } from '../customer/Customer'
import { LogisticItem } from './LogisticItem'
import type { LogisticStatusEnum } from './LogisticStatusEnum'
import { LogisticVendorOrShipToEnum } from './LogisticVendorOrShipToEnum'
import { Order } from '../order/Order'
import { RelatedEntity } from '../base/RelatedEntity'
import { Activity } from '../activity/Activity'
import { DocumentLock } from '../documentLock/DocumentLock'
import { WorkOrder } from '../work-order/WorkOrder'
import { Asset } from '../asset/Asset'
import { Shipping } from '../shipping/Shipping'
import { Communication } from '../communication/Communication'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('LogisticInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class Logistic extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('lo') })
  declare _id: string

  @Field(() => Date)
  @Prop({ type: Date })
  shipDate: Date

  @Field(() => String)
  @Prop({ type: String })
  shipVia: string

  @Field(() => Customer)
  @Prop({ type: Customer })
  vendor: Customer

  @Field(() => Address)
  @Prop({ type: Address })
  vendorAddress: Address

  @Field(() => Customer)
  @Prop({ type: Customer })
  shipTo: Customer

  @Field(() => Address)
  @Prop({ type: Address })
  shipToAddress: Address

  @Field(() => [LogisticItem])
  @Prop({ type: () => [LogisticItem] })
  items: LogisticItem[]

  @Field(() => Float)
  @Prop({ type: Number })
  totalQuantity: number

  @Field(() => String)
  @Prop({ type: String })
  declare status: LogisticStatusEnum

  @Field(() => [String])
  @Prop({ type: [String] })
  instructions: string[]

  @Field(() => Date)
  @Prop({ type: Date })
  expectedFinishDate: Date

  @Field(() => [Order])
  @Prop({ type: [String] })
  relatedSalesOrders: Order[]

  /**
   * These are the _id of orders that are related to this logistic
   */
  @Field(() => [Logistic])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedLogistics: ReturnType<() => Logistic[]>

  @Field(() => [WorkOrder])
  @Prop({ type: [RelatedEntity] }, PropType.ARRAY)
  relatedWorkOrders: ReturnType<() => WorkOrder[]>

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  /**
   * The _id of the task whose action hook created this logistic.
   * Used by CreateLogisticActionHook to track which logistic belongs to which task.
   */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  createdByTaskId?: string

  //This has field resolver, but we put it here for type-safety too
  @Field(() => DocumentLock)
  documentLock: DocumentLock

  @Field(() => Shipping)
  @Prop({ type: Shipping })
  shipping: Shipping

  //which party the carrier collects from; an absent value on a pre-existing record is treated as vendor
  @Field(() => String)
  @Prop({ type: String, default: LogisticVendorOrShipToEnum.vendor })
  shippingOrigin: LogisticVendorOrShipToEnum

  //This has field resolver, but we put it here for type-safety too
  @Field(() => Float)
  totalWeight: number

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isManualShippingRate: boolean

  @Field(() => String)
  @Prop({ type: String })
  manualShippingCarrier: string

  @Field(() => Communication)
  @Prop({ type: Communication })
  communication: Communication

  // Channel-neutral conversation handle (field resolver): the linked conversation's externalId.
  // Lets the communication UI mount the thread without re-reading anything else.
  @Field(() => String, { nullable: true })
  activeConversationId?: string

  /**
   * Sum of the rounded item `net` amounts, rounded to two decimal places. Computed by
   * computeLogisticAmount in the repository's preSave hook.
   */
  @Field(() => Float)
  @Prop({ type: Number })
  gross: number

  /**
   * `gross` adjusted by the carrier charge when `isShippingCostIncluded` is set, rounded to
   * two decimal places. Computed by computeLogisticAmount in the repository's preSave hook.
   */
  @Field(() => Float)
  @Prop({ type: Number })
  net: number

  //whether the carrier charge (shipping.shippingCost) is added into `net`, or left off the quote entirely
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isShippingCostIncluded: boolean
}
